import React from 'react';
import { Container } from 'types/Watchlist';

/**
 * Describes a selection that consists of the containers only on the current page of the table
 */
export interface PageSelection {
  model: 'page';
  containers: Container[];
  /** The total number of containers for this filter; not the selection */
  totalCount: number;
}

/**
 * Describes a selection that is defined by the current filter and spans across all the pages of a table
 */
export interface FilterSelection {
  model: 'filter';
  /** The total number of containers for this filter */
  totalCount: number;
}

/**
 * Default when there is no selection
 */
export interface NoSelection {
  model: 'none';
}

export type SelectionModel = NoSelection | PageSelection | FilterSelection;

interface SelectionContextType {
  selection: SelectionModel;
  setSelection: React.Dispatch<React.SetStateAction<SelectionModel>>;
}

export const SelectionContext = React.createContext<SelectionContextType>({
  selection: { model: 'none' },
  setSelection: () => {},
});

interface Props {
  children: JSX.Element;
}

export const SelectionContextProvider: React.FC<Props> = ({ children }) => {
  const [selection, setSelection] = React.useState<SelectionModel>({
    model: 'none',
  });

  const value = React.useMemo(
    () => ({
      selection,
      setSelection,
    }),
    [selection]
  );

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
};

export const useSelectionContext = () => {
  const context = React.useContext(SelectionContext);
  return context;
};

export const useSelectionCount = (selection: SelectionModel) => {
  const count = React.useMemo(() => {
    switch (selection.model) {
      case 'filter':
        return selection.totalCount;
      case 'page':
        return selection.containers.length;
      default:
        return 0;
    }
  }, [selection]);
  return count;
};
